"use client";

import React from "react";
import Container from "@/components/layout/Container";
import Flex from "@/components/layout/Flex";
import Carousel, { type CarouselOptions } from "@/components/media/Carousel";
import Headline from "@/components/project/Headline";
import Spacer from "@/components/shared/Spacer";
import Visibility from "@/components/shared/Visibility";
import Button from "@/components/ui/Button";
import { URLS } from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import { useGalleryFilters } from "../provider";
import type { GalleryProps } from "../types";
import ContactGalleryCard from "./gallery-contact-us-item";
import GalleryFilter from "./gallery-filter";
import GalleryItem from "./gallery-item";

function GalleryRoot({
  items,
  presetRatio,
  showHeader = true,
  showAction = true,
  galleryItemProps = {},
  galleryContactUsItemProps = {},
  containerProps = {},
  className,
  ...props
}: GalleryProps) {
  const { selectedTags } = useGalleryFilters();
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const carouselOptions: CarouselOptions = React.useMemo(
    () => ({
      inViewThreshold: 0.5,
      align: "start",
      loop: true,
    }),
    [],
  );

  const filteredItems = React.useMemo(() => {
    if (!items?.length || selectedTags.size === 0) {
      return items ?? [];
    }

    return items.filter(item =>
      [...item.tags].some(tag => selectedTags.has(tag)),
    );
  }, [items, selectedTags]);

  return (
    <Container
      asChild
      maxWidth="max-w-md"
      md="md:max-w-3xl"
      lg="lg:max-w-6xl"
      {...containerProps}
    >
      <Flex.Column asChild items="start">
        <Carousel
          id="services-gallery"
          options={carouselOptions}
          className={cn("w-full", className, classes)}
          {...native}
        >
          <Visibility shouldShow={showHeader}>
            <Flex.Row fill justify="between">
              <Headline gap="gap-2" as="H4">
                Services
              </Headline>

              <GalleryFilter />
            </Flex.Row>
          </Visibility>

          <Carousel.Content className="w-full">
            {filteredItems.map(
              (
                { label, description, tags, href, imageUrl, imageAlt },
                index,
              ) => (
                <GalleryItem
                  key={label}
                  index={index}
                  label={label}
                  description={description}
                  tags={tags}
                  url={imageUrl}
                  alt={imageAlt}
                  href={href}
                  presetRatio={presetRatio}
                  {...galleryItemProps}
                />
              ),
            )}

            <ContactGalleryCard
              index={filteredItems.length}
              presetRatio={presetRatio}
              {...galleryContactUsItemProps}
            />
          </Carousel.Content>

          <Spacer size="h-4" />

          <Flex.Column justify="center" items="start">
            <Carousel.Counter />

            <Visibility shouldShow={showAction}>
              <Button.Icon
                label="SEE ALL OUR SERVICES"
                variant="default"
                icon="arrow-right"
                href={URLS.SERVICES.ROOT}
                orientation="right"
                aria-label="See all our services"
              />
            </Visibility>
          </Flex.Column>
        </Carousel>
      </Flex.Column>
    </Container>
  );
}

export default GalleryRoot;
